import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { Wordmark } from "../components/Wordmark.js";
import { auth } from "../lib/api.js";
import { useAuth } from "../lib/auth-context.js";

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  google_denied: "Google sign-in was cancelled.",
  missing_code: "Google sign-in did not complete. Please try again.",
  expired_or_missing_cookie: "That sign-in attempt took too long or expired. Please try again.",
  state_mismatch: "That sign-in link was invalid or already used. Please try again.",
  google_auth_error: "Google could not verify your account. Please try again.",
  internal_error: "Something went wrong signing you in. Please try again.",
};

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    auth
      .config()
      .then((c) => setGoogleEnabled(c.googleSignInEnabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  useEffect(() => {
    const code = searchParams.get("error");
    if (code) setError(OAUTH_ERROR_MESSAGES[code] ?? "Sign-in failed. Please try again.");
  }, [searchParams]);

  if (!loading && user) return <Navigate to="/" replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 340,
          background: "var(--surface-1)",
          border: "1px solid var(--border)",
          padding: 28,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <Wordmark animated size={26} />
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: 12,
              letterSpacing: "0.14em",
              margin: "14px 0 0",
            }}
          >
            AUTHENTICATION REQUIRED
          </p>
        </div>

        {error && (
          <p
            role="alert"
            style={{
              color: "var(--status-critical)",
              fontSize: 13,
              marginBottom: 16,
              padding: "8px 10px",
              background: "color-mix(in srgb, var(--status-critical) 10%, var(--surface-1))",
            }}
          >
            {error}
          </p>
        )}

        {googleEnabled && (
          <>
            {/* A real navigation, not a fetch/onClick -- OAuth has to leave
                the SPA and follow redirects through Google and back. */}
            <a href="/api/auth/google/start" style={googleButtonStyle}>
              <GoogleIcon />
              SIGN IN WITH GOOGLE
            </a>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                margin: "18px 0",
                color: "var(--text-muted)",
                fontSize: 12,
              }}
            >
              <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
              OR
              <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>&gt; LOGIN</label>
          <input
            type="email"
            required
            autoFocus={!googleEnabled}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />

          <label style={{ ...labelStyle, marginTop: 14 }}>&gt; PASSCODE</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />

          <button type="submit" disabled={submitting} style={buttonStyle}>
            {submitting ? "[ AUTHENTICATING... ]" : "[ ENTER ]"}
          </button>
        </form>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden focusable="false">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid var(--border)",
  background: "var(--surface-raised)",
  color: "var(--text-primary)",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "var(--text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.08em",
};

const buttonStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 22,
  padding: "9px 0",
  fontSize: 13,
  letterSpacing: "0.12em",
  color: "var(--page-plane)",
  background: "var(--text-primary)",
  border: "1px solid var(--text-primary)",
  cursor: "pointer",
};

const googleButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  padding: "9px 0",
  fontSize: 14,
  fontWeight: 500,
  color: "var(--text-primary)",
  background: "var(--surface-raised)",
  border: "1px solid var(--border)",
  textDecoration: "none",
  boxSizing: "border-box",
  letterSpacing: "0.08em",
};
